import check from '../libraries/check-types-ext';
import AppError from '../libraries/AppError';
import ProductMapper from './ProductMapper';
import Category from './Category';
import { createSeoOptimizedProductName, filterProductDiscounts } from '../libraries/Utils';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { TransientReleaseToggles } from '../transientReleaseToggles/TransientReleaseToggles';
import * as _ from 'lodash';
const { LRUCache } = require('lru-cache');
const productCatalogCache = LRUCache
    ? new LRUCache({
        max: 150,
        ttl: 60 * 60 * 1000,
    })
    : undefined;
export default class ProductCatalog {
    constructor(productCatalogData) {
        this.instancedFromCache = false;
        ApmHelper.apmDecorator(() => {
            check.object.orFail(productCatalogData);
            const enableLRUCache = (() => {
                if (!productCatalogCache) {
                    return false;
                }
                if (!productCatalogData.products[0]) {
                    return false;
                }
                const transientReleaseToggles = TransientReleaseToggles(process.env['NODE_ENV'], productCatalogData.products[0].tenantId);
                return transientReleaseToggles.enableProductCatalogObjectInMemoryCaching();
            })();
            if (enableLRUCache && productCatalogData.productCatalogCacheKey) {
                const cachedProductCatalogInstance = productCatalogCache.get(productCatalogData.productCatalogCacheKey);
                if (cachedProductCatalogInstance instanceof ProductCatalog) {
                    this.categoryMap = cachedProductCatalogInstance.categoryMap;
                    this.productMap = cachedProductCatalogInstance.productMap;
                    this.productNameMap = cachedProductCatalogInstance.productNameMap;
                    this.productSeoOptimizedNameToIdMap = cachedProductCatalogInstance.productSeoOptimizedNameToIdMap;
                    this.departments = cachedProductCatalogInstance.departments;
                    this.priceRules = cachedProductCatalogInstance.priceRules;
                    this.selectedShopId = cachedProductCatalogInstance.selectedShopId;
                    this.pickupTimeZone = cachedProductCatalogInstance.pickupTimeZone;
                    this.userTimeZone = cachedProductCatalogInstance.userTimeZone;
                    this.currentDate = cachedProductCatalogInstance.currentDate;
                    this.correspondingRootProducts = cachedProductCatalogInstance.correspondingRootProducts;
                    this.activeProductMap = _.pickBy(cachedProductCatalogInstance.productMap, (p) => p.isActive === true);
                    this.instancedFromCache = true;
                }
            }
            if (!this.instancedFromCache) {
                check.array.orFail(productCatalogData.products, 'productCatalogData.products');
                check.array.orFail(productCatalogData.categories, 'productCatalogData.categories');
                const productMapper = new ProductMapper();
                const sortProducts = (p1, p2) => {
                    if (p1.isPartOfCompositeProduct === true) {
                        return -1;
                    }
                    else if (p2.isPartOfCompositeProduct === true) {
                        return 1;
                    }
                    return 0;
                };
                const extendedProducts = productCatalogData.products.map((product) => {
                    if (product.discount) {
                        product.discount = filterProductDiscounts(product, {
                            channel: productCatalogData.channel,
                            deliveryMethod: productCatalogData.deliveryMethod,
                            customerType: productCatalogData.customerType,
                        });
                    }
                    product.seoOptimizedName = createSeoOptimizedProductName(product.name);
                    return product;
                });
                extendedProducts.sort(sortProducts).map((productNode) => productMapper.getProductObject(productNode));
                this.productMap = productMapper.productMap;
                this.productNameMap = productMapper.productNameMap;
                this.productSeoOptimizedNameToIdMap = productMapper.productSeoOptimizedNameToIdMap;
                productCatalogData.categories
                    .filter((categoryNode) => !categoryNode.disabled)
                    .map((categoryNode) => new Category(categoryNode, productMapper));
                this.categoryMap = productMapper.categoryMap;
                this.departments = (productCatalogData.departments || []).map((department) => {
                    department.productIds = productCatalogData.products
                        .filter((p) => p.departmentId === department.id)
                        .map((p) => p.id);
                    return department;
                });
                this.correspondingRootProducts = productMapper.correspondingRootProducts;
                this.priceRules = productCatalogData.priceRules || [];
                this.selectedShopId = productCatalogData.selectedShopId;
                this.pickupTimeZone = productCatalogData.pickupTimeZone;
                this.userTimeZone = productCatalogData.userTimeZone;
                this.currentDate = productCatalogData.currentDate;
                if (enableLRUCache && productCatalogData.productCatalogCacheKey) {
                    productCatalogCache.set(productCatalogData.productCatalogCacheKey, this);
                }
                this.activeProductMap = _.pickBy(productMapper.productMap, (p) => p.isActive === true);
            }
        }, {
            apmLabel: 'ProductCatalogConstructor',
            apmType: ApmSpanType.PURE_FUNCTION,
        })();
    }
    get categories() {
        return this.getCategories();
    }
    get categoriesWithDisabled() {
        return this.getCategories();
    }
    get products() {
        return this.getAllProducts();
    }
    get productsWithDisabled() {
        return this.getAllProducts(true);
    }
    get mapper() {
        const productMapper = new ProductMapper();
        productMapper.productMap = this.productMap;
        productMapper.productNameMap = this.productNameMap;
        productMapper.productSeoOptimizedNameToIdMap = this.productSeoOptimizedNameToIdMap;
        productMapper.categoryMap = this.categoryMap;
        productMapper.correspondingRootProducts = this.correspondingRootProducts;
        return productMapper;
    }
    getAllProducts(includeDisabled = false) {
        return Object.values(includeDisabled ? this.productMap : this.activeProductMap);
    }
    getAllProductsByDepartment(departmentId, includeDisabled = false) {
        return this.getAllProducts(includeDisabled).filter((p) => p.departmentId === departmentId);
    }
    getProducts(productIds, includeDisabled = false) {
        return _.pick(includeDisabled ? this.productMap : this.activeProductMap, productIds);
    }
    getProduct(productId, includeDisabled = false) {
        const productMap = includeDisabled ? this.productMap : this.activeProductMap;
        const realProductId = (() => {
            if (productId && typeof productId === 'object') {
                check.string.orFail(productId.productId, 'productId');
                return productId.productId;
            }
            else {
                check.string.orFail(productId, 'productId');
                return productId;
            }
        })();
        return ApmHelper.apmDecorator(() => {
            if (!productMap[realProductId]) {
                throw AppError.ProductIsUndefinedError().addData('productId', realProductId);
            }
            const product = productMap[realProductId];
            if (!product.id) {
                throw AppError.ProductReferenceError().addData('product', realProductId);
            }
            return product;
        }, {
            apmLabel: 'ProductMapperGetProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    getProductByName(_name, includeDisabled = false) {
        const name = _name ? _name.toLowerCase() : '';
        if (!this.productNameMap[name]) {
            throw AppError.ProductIsUndefinedError().addData('name', name);
        }
        return this.getProduct(this.productNameMap[name], includeDisabled);
    }
    getProductBySeoOptimizedName(_name, includeDisabled = false) {
        const name = _name ? _name.toLowerCase() : '';
        if (!this.productSeoOptimizedNameToIdMap[name]) {
            throw AppError.ProductIsUndefinedError().addData('name', name);
        }
        return this.getProduct(this.productSeoOptimizedNameToIdMap[name], includeDisabled);
    }
    getProductBySeoNameOrName(name, includeDisabled = false) {
        try {
            return this.getProductBySeoOptimizedName(name, includeDisabled);
        }
        catch (e) {
            return this.getProductByName(name, includeDisabled);
        }
    }
    getProductName(productId, includeDisabled = false) {
        return this.getProduct(productId, includeDisabled).name;
    }
    getProductAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProduct(productId, includeDisabled));
    }
    getProductByNameAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProductByName(productId, includeDisabled));
    }
    getProductBySeoOptimizedNameAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProductBySeoOptimizedName(productId, includeDisabled));
    }
    getProductBySeoNameOrNameAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProductBySeoNameOrName(productId, includeDisabled));
    }
    getCategories() {
        return Object.values(this.categoryMap);
    }
    getCategory(categoryId) {
        if (!this.categoryMap[categoryId]) {
            throw AppError.CategoryIsUndefinedError().addData('categoryId', categoryId);
        }
        return this.categoryMap[categoryId];
    }
    updateCategory() {
        throw AppError.createNewError('Product catalog object is read only, please recreate new one');
    }
    getHomePageCategory({ shopId = undefined, channel = undefined } = {}) {
        const homeCategories = this.getCategories().filter((category) => category.isHomeCategory === true);
        const filteredChannelHomeCategories = homeCategories.filter((category) => {
            if (!Array.isArray(category.isSpecificForChannels)) {
                return false;
            }
            if (!category.isSpecificForChannels.includes(channel)) {
                return false;
            }
            return true;
        });
        const filteredShopHomeCategories = (filteredChannelHomeCategories.length > 0 ? filteredChannelHomeCategories : homeCategories).filter((c) => {
            if (shopId !== undefined) {
                if (!Array.isArray(c.isSpecificForShops)) {
                    return false;
                }
                if (!c.isSpecificForShops.includes(shopId)) {
                    return false;
                }
            }
            return true;
        });
        if (filteredShopHomeCategories.length > 0) {
            return filteredShopHomeCategories[0];
        }
        else if (filteredChannelHomeCategories.length > 0) {
            return filteredChannelHomeCategories[0];
        }
        else {
            return homeCategories[0];
        }
    }
    getDepartments() {
        return this.departments;
    }
    getDepartmentsWithCallback(callback) {
        callback(this.departments);
    }
    getDepartmentsAsync() {
        return Promise.resolve(this.getDepartments());
    }
    getDepartment(departmentId) {
        return this.departments.find((d) => d.id === departmentId);
    }
    getPickupTimeZone() {
        return this.pickupTimeZone;
    }
    getCategoryFromProduct(product) {
        const objTypes = ['Category', 'DropDownMenuElement'];
        return this.getCategories()
            .filter((cat) => objTypes.includes(cat.objType))
            .find((category) => {
            return !!category.elements.find((elements) => {
                return 'items' in elements && Array.isArray(elements.items)
                    ? !!elements.items.find((item) => {
                        if (item && typeof item === 'object') {
                            if ('id' in item && item.id) {
                                return product.id === item.id;
                            }
                            if ('node' in item && item.node && item.node.id_ref) {
                                return product.id === item.node.id_ref;
                            }
                        }
                        return undefined;
                    })
                    : undefined;
            });
        });
    }
    getAllCategoriesOfProduct(productID) {
        const objTypes = ['Category', 'DropDownMenuElement'];
        return this.getCategories()
            .filter((c) => objTypes.includes(c.objType))
            .filter((category) => !!category.elements.find((elements) => {
            if ('items' in elements && Array.isArray(elements.items)) {
                return elements.items.find((item) => {
                    if (item && typeof item === 'object') {
                        if ('id' in item && item.id) {
                            return productID === item.id;
                        }
                        if ('node' in item && item.node && item.node.id_ref) {
                            return productID === item.node.id_ref;
                        }
                    }
                    return undefined;
                });
            }
            if ('props' in elements && elements.props && Array.isArray(elements.props.products)) {
                return elements.props.products.includes(productID);
            }
        }));
    }
    isInstancedFromCache() {
        return this.instancedFromCache;
    }
}
